import { useEffect, useMemo } from 'react';
import type { FC, ReactNode } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { Breadcrumb } from '@dynamon-as/ui';
import type {
    BreadcrumbItemConfig,
    BreadcrumbRenderLinkProps,
} from '@dynamon-as/ui';
import { useApiCall, useDepartment } from '@hooks';
import { useDynolang } from '@hooks';
import { cx } from '@lib/utils';
import { ShiftGeneratorTaskService } from '@api/ShiftGeneratorTaskService';
import { useNavigationGuard } from '@contexts';
import { useAuthStore } from '@auth/stores';
import { useTaskStore } from '@stores';
import { Tooltip } from '@dynamon-as/ui';
import { matchBreadcrumbRoute } from './breadcrumbsConfig';
import type { BreadcrumbResolveContext } from './breadcrumbsConfig';
import styles from './HeaderBreadcrumbs.module.css';

/** Route whose breadcrumb title is the task name. */
const TASK_DETAIL_PATTERNS = [
    '/departments/:departmentId/tasks/:taskId',
] as const;

const TASK_DETAIL_PATTERN_SET: ReadonlySet<string> = new Set(
    TASK_DETAIL_PATTERNS,
);

interface BreadcrumbTitleProps {
    title: ReactNode;
    /** Help text anchored to the title; omitted when not configured. */
    tooltip?: string | undefined;
}

const BreadcrumbTitle: FC<BreadcrumbTitleProps> = ({ title, tooltip }) => {
    if (!tooltip) {
        return <>{title}</>;
    }

    return (
        <Tooltip
            text={tooltip}
            placement="bottom"
            delay={0}
        >
            <span>{title}</span>
        </Tooltip>
    );
};

/**
 * Route-driven breadcrumb trail rendered in the layout header
 * (@dynamon-as/ui BaseLayout/Header `breadcrumb` slot).
 *
 * The Breadcrumb `title` shows the current page and the trail below it
 * the ancestors (home icon › organization › department › parent section),
 * resolved from the current pathname via `BREADCRUMB_ROUTES`, so pages
 * no longer configure their own headers.
 */
const HeaderBreadcrumbs: FC = () => {
    const { pathname } = useLocation();
    const { departmentId, taskId } = useParams<{
        departmentId: string;
        taskId: string;
    }>();
    const { department } = useDepartment();
    const { guardedNavigate } = useNavigationGuard();
    const { t } = useDynolang('PAGE_ORG_AND_DEPARTMENTS');
    const selectedOrgId = useAuthStore((state) => state.selectedOrgId);
    const userOrganizations = useAuthStore((state) => state.userOrganizations);

    const match = useMemo(() => matchBreadcrumbRoute(pathname), [pathname]);

    const isTaskDetailRoute =
        !!match?.route.pattern &&
        TASK_DETAIL_PATTERN_SET.has(match.route.pattern);

    // The task name is only needed on the task detail route. Gating the
    // taskId keeps this fetch away from every other route (including
    // the result tabs, whose title does not show the task name).
    const { response: shiftGeneratorTask } = useApiCall(
        ShiftGeneratorTaskService.getShiftGeneratorTask,
        [departmentId, taskId],
        [departmentId, isTaskDetailRoute ? taskId : undefined],
    );

    // TODO: Move the task name resolution to the view logic once the
    // migration from the old header area to the new UI topbar is done.
    const taskNames = useTaskStore((state) => state.taskNames);
    const setTaskName = useTaskStore((state) => state.setTaskName);

    const fetchedTaskId = shiftGeneratorTask?.id;
    const fetchedTaskName = shiftGeneratorTask?.name;

    useEffect(() => {
        if (fetchedTaskId && fetchedTaskName) {
            setTaskName(fetchedTaskId, fetchedTaskName);
        }
    }, [fetchedTaskId, fetchedTaskName, setTaskName]);

    const taskName =
        (taskId ? taskNames[taskId] : undefined) ?? shiftGeneratorTask?.name;

    // On department routes the department's own organization is the
    // authoritative one; elsewhere fall back to the organization the
    // user has currently selected.
    const orgName =
        department?.org?.name ??
        userOrganizations?.find((org) => org.id === selectedOrgId)?.name;

    const context = useMemo<BreadcrumbResolveContext>(
        () => ({
            params: match?.params ?? {},
            orgName,
            departmentName: department?.name,
            taskName,
            t,
        }),
        [match, orgName, department?.name, taskName, t],
    );

    // TODO: Replace/cleanup useMemo where it can be improved (v19)
    const title = useMemo(() => {
        if (!match) {
            return '';
        }

        const { title: titleConfig } = match.route;
        return typeof titleConfig === 'function'
            ? titleConfig(context)
            : titleConfig;
    }, [match, context]);

    const items = useMemo<BreadcrumbItemConfig[]>(() => {
        if (!match) {
            return [];
        }

        const resolved: BreadcrumbItemConfig[] = match.route.segments.map(
            (segment) => {
                const label: ReactNode =
                    typeof segment.label === 'function'
                        ? segment.label(context)
                        : segment.label;
                const href = segment.href?.(context);
                return href !== undefined ? { label, href } : { label };
            },
        );

        // Hide segments whose dynamic labels have not loaded yet (e.g.
        // the organization name) instead of rendering empty separators.
        return resolved.filter((item) => item.label !== '');
    }, [match, context]);

    const linkItems = useMemo(
        () =>
            ({
                href,
                isActive,
                className,
                children,
            }: BreadcrumbRenderLinkProps): ReactNode => (
                <Link
                    to={href}
                    className={className}
                    aria-current={isActive ? 'page' : undefined}
                    onClick={(event) => {
                        event.preventDefault();
                        if (pathname !== href) {
                            guardedNavigate(href);
                        }
                    }}
                >
                    {children}
                </Link>
            ),
        [pathname, guardedNavigate],
    );

    if (!match || (items.length === 0 && title === '')) {
        return null;
    }

    // Help text for the current page stays anchored to its title.
    const activeTooltip = match.route.activeTooltip;
    const resolvedTooltip =
        typeof activeTooltip === 'function'
            ? activeTooltip(context)
            : activeTooltip;
    const titleNode =
        title === '' ? undefined : (
            <BreadcrumbTitle
                title={title}
                tooltip={resolvedTooltip}
            />
        );

    return (
        <Breadcrumb
            title={titleNode}
            items={items}
            renderLink={linkItems}
            ariaLabel={t(
                'navigation_section.breadcrumb_aria.label',
                'Brødsmulesti',
            )}
            className={cx(styles.trail)}
        />
    );
};

export default HeaderBreadcrumbs;
